import React, { useEffect, useState } from 'react';
import { X, Check, AlertCircle, Info } from 'lucide-react';
import { Button } from './ui/Button';
import { supabaseClient } from '../lib/supabase';
interface MenuItemOptionsModalProps {
  menuItem: any;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedOptions: any[], totalPrice: number) => void;
}
export function MenuItemOptionsModal({
  menuItem,
  isOpen,
  onClose,
  onConfirm
}: MenuItemOptionsModalProps) {
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  useEffect(() => {
    if (isOpen && menuItem?.id) {
      loadSections();
      setTotalPrice(menuItem.price);
      setSelections({});
      setShowValidationErrors(false);
    }
  }, [isOpen, menuItem]);
  const loadSections = async () => {
    setLoading(true);
    try {
      const data = await supabaseClient.getMenuItemSections(menuItem.id);
      // Sort sections by displayOrder
      const sortedData = (data || []).sort((a: any, b: any) => a.displayOrder - b.displayOrder);
      // Sort options within sections
      sortedData.forEach((section: any) => {
        if (section.options) {
          section.options.sort((a: any, b: any) => a.displayOrder - b.displayOrder);
        }
      });
      setSections(sortedData);
    } catch (error) {
      console.error('Error loading sections:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleSelection = (sectionId: string, optionId: string, section: any) => {
    setSelections(prev => {
      const currentSelections = prev[sectionId] || [];
      let newSelections: string[];
      // Logic for single selection (Radio behavior)
      if (section.maxSelections === 1) {
        // If it's already selected and not required (minSelections === 0), allow deselect
        if (currentSelections.includes(optionId) && section.minSelections === 0) {
          newSelections = [];
        } else {
          newSelections = [optionId];
        }
      }
      // Logic for multiple selection (Checkbox behavior)
      else {
        if (currentSelections.includes(optionId)) {
          newSelections = currentSelections.filter(id => id !== optionId);
        } else {
          // Check max selections
          if (currentSelections.length >= section.maxSelections) {
            // Optional: You could implement a "replace oldest" logic here,
            // but for now we just prevent adding more
            return prev;
          }
          newSelections = [...currentSelections, optionId];
        }
      }
      return {
        ...prev,
        [sectionId]: newSelections
      };
    });
  };
  // Calculate total price whenever selections change
  useEffect(() => {
    if (!menuItem) return;
    let modifiersTotal = 0;
    Object.entries(selections).forEach(([sectionId, optionIds]) => {
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        optionIds.forEach(optId => {
          const option = section.options.find((o: any) => o.id === optId);
          if (option) {
            modifiersTotal += option.priceModifier;
          }
        });
      }
    });
    setTotalPrice(menuItem.price + modifiersTotal);
  }, [selections, sections, menuItem]);
  const getSectionValidationState = (section: any) => {
    const selectedCount = (selections[section.id] || []).length;
    if (section.required && selectedCount < section.minSelections) {
      return {
        isValid: false,
        message: `Selecciona al menos ${section.minSelections}`
      };
    }
    if (selectedCount > section.maxSelections) {
      return {
        isValid: false,
        message: `Máximo ${section.maxSelections} opciones`
      };
    }
    return {
      isValid: true
    };
  };
  const validateAll = () => {
    for (const section of sections) {
      const {
        isValid
      } = getSectionValidationState(section);
      if (!isValid) return false;
    }
    return true;
  };
  const handleConfirm = () => {
    if (!validateAll()) {
      setShowValidationErrors(true);
      // Scroll to first error?
      return;
    }
    // Format selected options for the order
    const selectedOptions: any[] = [];
    Object.entries(selections).forEach(([sectionId, optionIds]) => {
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        optionIds.forEach(optId => {
          const option = section.options.find((o: any) => o.id === optId);
          if (option) {
            selectedOptions.push({
              sectionId: section.id,
              sectionName: section.name,
              optionId: option.id,
              optionName: option.name,
              priceModifier: option.priceModifier
            });
          }
        });
      }
    });
    onConfirm(selectedOptions, totalPrice);
  };
  if (!isOpen || !menuItem) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn" data-id="element-384">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 max-h-[90vh] flex flex-col animate-slideUp overflow-hidden" data-id="element-385">
        {/* Header Image & Title */}
        <div className="relative h-48 bg-gray-100 dark:bg-gray-800 shrink-0" data-id="element-386">
          {menuItem.image ? <img src={menuItem.image} alt={menuItem.name} className="w-full h-full object-cover" data-id="element-387" /> : <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700" data-id="element-388">
              <span className="text-5xl" data-id="element-389">🍽️</span>
            </div>}

          {/* Close Button */}
          <button onClick={onClose} className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full backdrop-blur-sm transition-colors z-10" data-id="element-390">
            <X size={20} data-id="element-391" />
          </button>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6 pt-12" data-id="element-392">
            <h2 className="text-2xl font-bold text-white leading-tight" data-id="element-393">
              {menuItem.name}
            </h2>
            {menuItem.description && <p className="text-white/90 text-sm mt-1 line-clamp-2" data-id="element-394">
                {menuItem.description}
              </p>}
            <div className="mt-2 text-white font-semibold" data-id="element-395">
              Base: RD$ {menuItem.price.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Content - Scrollable Sections */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-gray-50 dark:bg-gray-950" data-id="element-396">
          {loading ? <div className="flex flex-col items-center justify-center py-12" data-id="element-397">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black dark:border-white mb-4" data-id="element-398"></div>
              <p className="text-gray-500 dark:text-gray-400" data-id="element-399">
                Cargando opciones...
              </p>
            </div> : sections.length === 0 ? <div className="text-center py-12" data-id="element-400">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4" data-id="element-401">
                <Check size={32} className="text-gray-400" data-id="element-402" />
              </div>
              <p className="text-gray-900 dark:text-white font-medium" data-id="element-403">
                Todo listo
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1" data-id="element-404">
                Este plato no tiene opciones configurables.
              </p>
            </div> : sections.map(section => {
          const validation = getSectionValidationState(section);
          const hasError = showValidationErrors && !validation.isValid;
          const currentSelectionCount = (selections[section.id] || []).length;
          const isMaxReached = currentSelectionCount >= section.maxSelections;
          return <div key={section.id} className={`bg-white dark:bg-gray-900 rounded-xl border transition-all duration-200 overflow-hidden ${hasError ? 'border-red-300 dark:border-red-800 shadow-sm ring-1 ring-red-200 dark:ring-red-900/50' : 'border-gray-200 dark:border-gray-800'}`} data-id="element-405">
                  {/* Section Header */}
                  <div className={`p-4 border-b border-gray-100 dark:border-gray-800 ${hasError ? 'bg-red-50 dark:bg-red-900/10' : ''}`} data-id="element-406">
                    <div className="flex justify-between items-start gap-2" data-id="element-407">
                      <div data-id="element-408">
                        <h3 className={`font-bold text-lg ${hasError ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-white'}`} data-id="element-409">
                          {section.name}
                        </h3>
                        {section.description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-410">
                            {section.description}
                          </p>}
                      </div>
                      <div className="flex flex-col items-end gap-1" data-id="element-411">
                        {section.required ? <span className="text-[10px] font-bold uppercase tracking-wider bg-black text-white dark:bg-white dark:text-black px-2 py-1 rounded" data-id="element-412">
                            Obligatorio
                          </span> : <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 px-2 py-1 rounded" data-id="element-413">
                            Opcional
                          </span>}
                      </div>
                    </div>

                    {/* Requirements Text */}
                    <div className="flex items-center gap-2 mt-2 text-xs font-medium" data-id="element-414">
                      {section.maxSelections === 1 ? <span className="text-gray-500 dark:text-gray-400" data-id="element-415">
                          Selecciona 1 opción
                        </span> : <span className="text-gray-500 dark:text-gray-400" data-id="element-416">
                          Selecciona{' '}
                          {section.minSelections > 0 ? `mínimo ${section.minSelections}` : ''}
                          {section.minSelections > 0 && section.maxSelections < 100 ? ' y ' : ''}
                          {section.maxSelections < 100 ? `máximo ${section.maxSelections}` : ''}
                        </span>}

                      {hasError && <span className="text-red-600 dark:text-red-400 flex items-center gap-1 ml-auto" data-id="element-417">
                          <AlertCircle size={12} data-id="element-418" />
                          {validation.message}
                        </span>}
                    </div>
                  </div>

                  {/* Options List */}
                  <div className="p-2" data-id="element-419">
                    {section.options.filter((opt: any) => opt.available).map((option: any) => {
                const isSelected = (selections[section.id] || []).includes(option.id);
                const isDisabled = !isSelected && isMaxReached && section.maxSelections > 1;
                return <label key={option.id} className={`
                              relative flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 mb-1 last:mb-0
                              ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}
                              ${isSelected ? 'bg-gray-50 dark:bg-gray-800 ring-1 ring-black dark:ring-white z-10' : ''}
                            `} data-id="element-420">
                            <div className="flex items-center gap-3" data-id="element-421">
                              {/* Custom Checkbox/Radio UI */}
                              <div className={`
                                  w-5 h-5 rounded-full border flex items-center justify-center transition-all duration-200
                                  ${isSelected ? 'border-black dark:border-white bg-black dark:bg-white scale-110' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900'}
                                `} data-id="element-422">
                                {isSelected && <Check size={12} className="text-white dark:text-black" strokeWidth={3} data-id="element-423" />}
                              </div>

                              <span className={`font-medium ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-424">
                                {option.name}
                              </span>
                            </div>

                            <div className="flex items-center gap-2" data-id="element-425">
                              {option.priceModifier > 0 && <span className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-426">
                                  +RD$ {option.priceModifier.toFixed(2)}
                                </span>}
                            </div>

                            <input type="checkbox" // Using checkbox for everything to handle custom logic
                  name={section.id} checked={isSelected} disabled={isDisabled} onChange={() => handleSelection(section.id, option.id, section)} className="hidden" data-id="element-427" />
                          </label>;
              })}
                  </div>
                </div>;
        })}
        </div>

        {/* Footer - Total & Action */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20" data-id="element-428">
          <div className="flex justify-between items-end mb-4" data-id="element-429">
            <div data-id="element-430">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1" data-id="element-431">
                Total a pagar
              </p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight" data-id="element-432">
                RD$ {totalPrice.toFixed(2)}
              </p>
            </div>
            {showValidationErrors && !validateAll() && <div className="text-right text-xs text-red-600 dark:text-red-400 font-medium animate-pulse" data-id="element-433">
                Faltan opciones requeridas
              </div>}
          </div>

          <Button onClick={handleConfirm} fullWidth disabled={loading} size="lg" className="h-12 text-base shadow-lg hover:shadow-xl transition-all" data-id="element-434">
            Agregar al Pedido
          </Button>
        </div>
      </div>
    </div>;
}